import { Button, Grid } from "@material-ui/core";
import {
    Championship,
    Collections,
    Protest,
    RaceControl,
    Round,
    Track,
    useChampionshipDrivers,
    useCollection,
} from "api";
import { Form, useFormikContext } from "formik";
import { DateTime } from "luxon";
import React, { useContext, useEffect, useMemo } from "react";
import {
    InputFieldFormik,
    LoggedUserContext,
    SelectFieldFormik,
    SelectOption,
    SubmitButton,
} from "ui/componentes";
import { LIMIT_OF_DAYS_TO_PROTEST } from "ui/views/Protests/hooks";
import { ProtestedDriverSelection } from "ui/views/Protests/ProtestedDriverSelection";
import { ProtestInfractionSelection } from "ui/views/Protests/ProtestInfractionSelection";

const cancelForm = (resetForm, onClose) => () => {
    resetForm();
    if (onClose) {
        onClose();
    }
};

const ProtestForm = ({ onClose }) => {
    const {
        resetForm,
        values,
        setValues,
        isSubmitting,
        setFieldValue,
    } = useFormikContext<Protest>();
    const loggedUser = useContext(LoggedUserContext);
    const tracksById =
        useCollection<{ [key: string]: Track }>({
            collection: Collections.TRACKS,
        }) || {};

    const championships = useCollection<Championship[]>(
        {
            collection: Collections.CHAMPIONSHIPS,
            where: [
                ["public", "==", true],
                ["seasonId", "==", values.seasonId || "-1"],
            ],
            storeAs: `championships_to_protest_${values.seasonId || "-1"}`,
        },
        true
    );
    championships.sort((c1, c2) => (c1.name < c2.name ? -1 : 1));

    const championshipOptions = useMemo(() => {
        return championships.map<SelectOption>((champ) => ({
            label: champ.name,
            value: champ.id || "",
        }));
    }, [championships]);

    const [today, dateMinusThreeDays] = useMemo(() => {
        const dateMinusThreeDays = DateTime.local()
            .minus({ day: LIMIT_OF_DAYS_TO_PROTEST })
            .toFormat("yyyy-MM-dd");
        const today = DateTime.local().toFormat("yyyy-MM-dd");
        return [today, dateMinusThreeDays];
    }, []);

    const rounds = useCollection<Round[]>(
        {
            collection: Collections.ROUNDS,
            where: [
                ["championshipId", "==", values.championshipId || "-1"],
                ["date", ">=", dateMinusThreeDays],
                ["date", "<=", today],
            ],
            storeAs: `rounds_starts_at_${values.championshipId || "-1"}`,
        },
        true
    );

    rounds.sort(
        (r1, r2) =>
            DateTime.fromISO(r1.date).toMillis() -
            DateTime.fromISO(r2.date).toMillis()
    );

    const roundOptions = useMemo(() => {
        return rounds.map<SelectOption>((round) => ({
            label: `${
                tracksById[round.trackId].friendlyName
            } - ${DateTime.fromISO(round.date).toFormat("dd/MM/yyyy")}`,
            value: round.id || "",
        }));
    }, [rounds, tracksById]);

    const races = useCollection<RaceControl[]>(
        {
            collection: Collections.RACES_CONTROL,
            where: ["roundId", "==", values.roundId],
            storeAs: `races_control_${values.roundId || "-1"}`,
        },
        true
    );

    const raceOptions = useMemo(() => {
        return races
            .sort((r1, r2) => (r1.raceNumber || 0) - (r2.raceNumber || 0))
            .map<SelectOption>((raceControl) => ({
                label: `Bateria ${raceControl.raceNumber}`,
                value: raceControl.id || "",
            }));
    }, [races]);

    useEffect(() => {
        setValues({
            ...values,
            championshipId: "",
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.seasonId]);

    useEffect(() => {
        setValues({
            ...values,
            roundId: "",
            raceControlId: "",
            protestedDriverId: "",
            protestedDriverNumber: "",
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.championshipId]);

    useEffect(() => {
        const race = races.filter((race) => race.id === values.raceControlId);
        if (race && race.length > 0) {
            setFieldValue("raceNumber", race[0].raceNumber);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.raceControlId]);

    const drivers = useChampionshipDrivers(values.championshipId);
    const driversWithoutLoggedUser = (drivers || []).filter(
        (d) => d.userId !== loggedUser.id
    );

    return (
        <Form>
            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <SelectFieldFormik
                        autoFocus
                        required
                        name="championshipId"
                        label="Campeonato"
                        options={championshipOptions}
                        error={!!values.championshipId && rounds.length === 0}
                        helperText={
                            !!values.championshipId && rounds.length === 0
                                ? "Nenhuma etapa disponível para abertura de protesto para esse campeonato"
                                : ""
                        }
                    />
                </Grid>
                <Grid item xs={12}>
                    <SelectFieldFormik
                        required
                        name="roundId"
                        label="Etapa"
                        options={roundOptions}
                    />
                </Grid>
                <Grid item xs={12} md={9}>
                    <SelectFieldFormik
                        required
                        name="raceControlId"
                        label="Bateria"
                        options={raceOptions}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <InputFieldFormik
                        required
                        id="lap"
                        name="lap"
                        label="Volta"
                        type="number"
                    />
                </Grid>
                <Grid item xs={12}>
                    <InputFieldFormik
                        required
                        id="complaint"
                        name="complaint"
                        label="Descrição do incidente"
                        multiline
                        rows={6}
                        helperText={`Max.: ${values.complaint.length} / 300`}
                    />
                </Grid>
                <Grid item xs={12}>
                    <ProtestInfractionSelection seasonId={values.seasonId} />
                </Grid>
                <Grid item xs={12} style={{ marginBottom: 10 }}>
                    <ProtestedDriverSelection
                        drivers={driversWithoutLoggedUser}
                    />
                </Grid>
                <Grid item md={6} xs={12}>
                    <Button
                        fullWidth
                        disabled={isSubmitting}
                        onClick={cancelForm(resetForm, onClose)}
                    >
                        Cancelar
                    </Button>
                </Grid>
                <Grid item md={6} xs={12}>
                    <SubmitButton fullWidth>
                        {isSubmitting ? "Enviando protesto" : "Enviar protesto"}
                    </SubmitButton>
                </Grid>
            </Grid>
        </Form>
    );
};

export { ProtestForm };
